import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCartStore } from '@/store/cartStore';
import { useTranslation } from 'react-i18next';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, profile, signOut } = useAuth();
  const items = useCartStore((state) => state.items);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Check if we're in staging environment
  const isStaging = import.meta.env.VITE_APP_ENV === 'staging' || 
                   window.location.hostname.includes('staging') ||
                   window.location.hostname.includes('staging--lokalaproducenter') ||
                   window.location.hostname.includes('--lokalaproducenter');

  // Note: Staging indicator shows when VITE_APP_ENV=staging OR hostname contains 'staging'
  // This helps developers and testers easily identify staging environment
  // The indicator will NOT appear in production
  
  // Debug logging (always log for debugging this issue)
  console.log('=== NAVBAR DEBUG ===');
  console.log('Current URL:', window.location.href);
  console.log('Hostname:', window.location.hostname);
  console.log('Environment Variables:', {
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
  });
  console.log('isStaging:', isStaging);
  console.log('=== END DEBUG ===');

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error in handleSignOut:', err);
      // Still navigate away since AuthContext should have cleared the local auth state
      navigate('/');
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* Staging Environment Indicator */}
            {isStaging && (
              <div className="mr-3 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-md shadow-md border-2 border-orange-600 animate-pulse">
                STAGING
              </div>
            )}
            <Link to="/" className="text-xl font-bold text-green-700" data-testid="logo">
              Lokala<span className="text-green-500">producenter</span>
            </Link>
            <div className="hidden md:ml-6 md:flex space-x-4" data-testid="desktop-nav">
              <Link to="/" className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-green-600" data-testid="nav-home">
                {t('common.home')}
              </Link>
              <Link to="/products" className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-green-600" data-testid="nav-products">
                {t('common.products')}
              </Link>
              <Link to="/producers" className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-green-600" data-testid="nav-producers">
                {t('common.producers')}
              </Link>
              <Link to="/faq" className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-green-600" data-testid="nav-faq">
                {t('common.faq')}
              </Link>
            </div>
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            {user ? (
              <>
                {/* Shopping cart moved before admin and logout */}
                <Link to="/cart" className="relative hover:text-green-600" data-testid="cart-link">
                  <ShoppingCart size={20} />
                  {itemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                      {itemCount}
                    </span>
                  )}
                </Link>
                
                {/* Show Admin Portal Link */}
                {profile?.role === 'admin' && (
                  <Link to="/admin" className="hover:text-green-600 font-medium text-green-700" data-testid="admin-link">
                    {t('common.admin')}
                  </Link>
                )}
                
                {/* Show Producer Portal Link */}
                {profile?.role === 'producer' && (
                  <Link to="/producer" className="hover:text-green-600 font-medium text-green-700" data-testid="producer-link">
                    {t('common.producer')}
                  </Link>
                )}
                
                {/* Show Account Link for customers */}
                {profile?.role === 'customer' && (
                  <Link to="/account/profile" className="hover:text-green-600 font-medium text-green-700 flex items-center" data-testid="account-link">
                    <User size={18} className="mr-1" />
                    {t('common.myAccount')}
                  </Link>
                )}
                
                <button 
                  onClick={handleSignOut} 
                  className="hover:text-green-600 font-medium"
                  data-testid="sign-out-button"
                >
                  {t('common.signOut')}
                </button>
              </>
            ) : (
              <>
                <Link to="/cart" className="relative hover:text-green-600" data-testid="cart-link">
                  <ShoppingCart size={20} />
                  {itemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                      {itemCount}
                    </span>
                  )}
                </Link>
                <Link to="/login" className="hover:text-green-600 font-medium" data-testid="sign-in-link">{t('common.signIn')}</Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden ml-auto text-gray-800" 
            onClick={toggleMenu} 
            data-testid="menu-button"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {/* Staging Environment Indicator for Mobile */}
              {isStaging && (
                <div className="px-3 py-2 mb-2">
                  <div className="inline-block px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-md shadow-md border-2 border-orange-600 animate-pulse">
                    STAGING MILJÖ
                  </div>
                </div>
              )}
              <Link 
                to="/" 
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                onClick={toggleMenu}
              >
                {t('common.home')}
              </Link>
              <Link 
                to="/products"
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                onClick={toggleMenu}
              >
                {t('common.products')}
              </Link>
              <Link 
                to="/producers"
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                onClick={toggleMenu}
              >
                {t('common.producers')}
              </Link>
              <Link 
                to="/faq"
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                onClick={toggleMenu}
              >
                {t('common.faq')}
              </Link>
              <Link 
                to="/cart"
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                onClick={toggleMenu}
              >
                {t('common.cart')} {itemCount > 0 && `(${itemCount})`}
              </Link>

              {user ? (
                <>
                  {profile?.role === 'admin' && (
                    <Link 
                      to="/admin"
                      className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                      onClick={toggleMenu}
                    >
                      {t('common.admin')}
                    </Link>
                  )}
                  
                  {profile?.role === 'producer' && (
                    <Link 
                      to="/producer"
                      className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                      onClick={toggleMenu}
                    >
                      {t('common.producer')}
                    </Link>
                  )}

                  {profile?.role === 'customer' && (
                    <Link
                      to="/account/profile"
                      className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                      onClick={toggleMenu}
                    >
                      {t('common.myAccount')}
                    </Link>
                  )}

                  <button 
                    onClick={() => {
                      handleSignOut();
                      toggleMenu();
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                  >
                    {t('common.signOut')}
                  </button>
                </>
              ) : (
                <Link 
                  to="/login"
                  className="block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-100"
                  onClick={toggleMenu}
                >
                  {t('common.signIn')}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;